import React, { useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, gql } from '@apollo/client';
import * as Sentry from '@sentry/react';

import {
  Root,
  Container,
  ImageSection,
  ImageGallery,
  ThumbnailList,
  Thumbnail,
  MainImage,
  DetailsSection,
  BrandName,
  ClassName,
  Points,
  DetailsList,
  DetailsItem,
  DetailsLabel,
  DetailsValue,
  SaveButton,
  FavoriteButton,
  ButtonGroup,
  TicketNote,
  SectionTitle,
  DescriptionSection,
  BulletList,
} from './EducationClassDetail.style';

import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { Icon } from '../../../ui/Icon/Icon';
import { Typography } from '../../../ui/Typography/Typography';
import { apiDomain, apiSiteId } from '../../../config';
import {
  toast,
  ToastNotification,
} from '../../../ui/ToastNotification/ToastNotification';
import { UserContext } from '../../../context/UserContext';
import { GET_LOYALTY_TIER } from '../../../graphql/queries/me';
import {
  normalizeLoyaltyTier,
  formatEligibleTiersMessage,
} from '../../../utils/clubhTier';
import {
  GET_WISHLIST,
  ADD_TO_WISHLIST,
  REMOVE_FROM_WISHLIST,
} from '../../RewardsView/rewardsViewConfig';

// GraphQL query to fetch education reward by slug
const GET_EDUCATION_REWARD = gql`
  query GetEducationReward($slug: String!, $locale: String) {
    content {
      findBySlug(slug: $slug, locale: $locale) {
        ... on EducationReward {
          _id
          n
          desc
          sl
          type
          brand {
            _id
            n
          }
          gallery {
            media {
              _id
            }
          }
          data {
            category
            idh
            distributor
            locationName
            province
            city
            dateTime
            inventoryLimit
            redemptionLimit
            bookingLink
            subCategory
            qualifyingTiers
          }
          ecommerce {
            price {
              amount
              currency
            }
            fullPrice {
              amount
              currency
            }
          }
        }
      }
    }
  }
`;

/* TODO: DELETE MOCK DATA AFTER TESTING - Mock data for fallback during development
const mockClassData = {
  'academy-class-1': {
    id: 'academy-class-1',
    name: 'Class Name Here',
    brand: 'SCHWARZKOPF PROFESSIONAL',
    brandSlug: 'schwarzkopf',
    points: 200000,
    category: 'academy-education',
    classType: 'Academy Education',
    location: '2515 Meadowpine Blvd, Mississauga, ON L5N 6C3',
    date: 'January 14, 2026',
    time: '9am - 5pm',
    distributor: 'Lorem ipsum',
    classNumber: '123456',
    bookingLink: null,
    images: [
      'https://picsum.photos/604/752?random=1',
      'https://picsum.photos/604/752?random=2',
      'https://picsum.photos/604/752?random=3',
      'https://picsum.photos/604/752?random=4',
    ],
    whatYouLearn: `Behind every thriving salon is a leader who understands the story their numbers are telling. Your financials aren't just spreadsheets—they're a roadmap to growth, stability, and the future you envision. In this session, we'll show you how to see the meaning behind the metrics, uncover opportunities you didn't know existed, and make confident decisions that fuel your salon's success.

You'll also meet Strategies—your partner in business growth—and discover how, through our collaboration with Club H, we empower salon owners with proven systems, hands-on support, and tools that turn business challenges into wins. This is your chance to step into the driver's seat of your business with clarity, confidence, and a renewed vision for what's possible.`,
    walkAwayWith: [
      'A clear understanding of the key numbers that drive salon success.',
      'Confidence in using your financials to make growth-focused decisions.',
      'Practical tools and resources to support long-term stability and expansion.',
    ],
    exclusiveInsight: '*Insight into exclusive Henkel-owner benefits that help you thrive',
  },
};
*/

export const EducationClassDetail = () => {
  const { slug } = useParams();
  const intl = useIntl();
  const locale = intl.locale;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { user } = useContext(UserContext);


  // Fetch education reward data from API
  const { loading, error, data } = useQuery(GET_EDUCATION_REWARD, {
    variables: { slug, locale },
    fetchPolicy: 'cache-first',
  });

  const { data: loyaltyTierData, loading: loyaltyTierLoading } = useQuery(GET_LOYALTY_TIER, {
    skip: user?._id == null,
    fetchPolicy: 'cache-first',
  });

  const { data: wishlistData } = useQuery(GET_WISHLIST, {
    skip: user?._id == null,
    variables: { locale },
    fetchPolicy: 'cache-first',
  });

  const [addToWishlist] = useMutation(ADD_TO_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  const [removeFromWishlist] = useMutation(REMOVE_FROM_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  // Transform API data to match component format
  const educationReward = data && data.content && data.content.findBySlug;
  
  if (loading) {
    return (
      <Root>
        <Container style={{ textAlign: 'center', padding: '40px' }}>
          <FormattedMessage defaultMessage="Loading..." />
        </Container>
      </Root>
    );
  }

  if (error || !educationReward) {
    return (
      <Root>
        <Container style={{ textAlign: 'center', padding: '40px' }}>
          <FormattedMessage defaultMessage="Class not found" />
        </Container>
      </Root>
    );
  }

  // Transform API data to component format
  const educationData = educationReward.data || {};
  
  // Determine if this is an event or a class
  const isEvent = educationData.category === 'events-experiences';
  
  // Format date/time
  let formattedDate = '';
  let formattedTime = '';
  if (educationData.dateTime) {
    const dt = new Date(educationData.dateTime);
    formattedDate = dt.toLocaleDateString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    formattedTime = dt.toLocaleTimeString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  // Format location
  const locationParts = [];
  if (educationData.locationName) locationParts.push(educationData.locationName);
  if (educationData.city) locationParts.push(educationData.city);
  if (educationData.province) locationParts.push(educationData.province);
  const formattedLocation = locationParts.join(', ') || 'TBD';

  // Format images
  const images = (educationReward.gallery || []).map(item =>
    item && item.media && item.media._id
      ? `https://${apiDomain}/${apiSiteId}/asset/${item.media._id}?type=fill&width=604&height=752`
      : 'https://picsum.photos/604/752?random=1'
  );

  // Fallback image if no images
  if (images.length === 0) {
    images.push('https://picsum.photos/604/752?random=1');
  }

  // Parse description for "What you'll learn" and bullet points
  const description = educationReward.desc || '';
  
  // Simple parsing: split by double newlines or bullet points
  // You may need to adjust this based on how descriptions are structured in CMS
  const descriptionParts = description.split('\n\n');
  const whatYouLearn = descriptionParts[0] || 'Details coming soon.';
  
  // Extract bullet points (lines starting with -, *, or •)
  const walkAwayWith = description
    .split('\n')
    .filter(line => /^[\-\*•]\s/.test(line.trim()))
    .map(line => line.replace(/^[\-\*•]\s/, '').trim());
  
  // If no bullet points found, provide defaults
  const walkAwayWithList = walkAwayWith.length > 0 ? walkAwayWith : [
    'Hands-on learning experience',
    'Expert guidance and techniques',
    'Networking opportunities',
  ];

  // Class type label
  const classType = isEvent 
    ? intl.formatMessage({ defaultMessage: 'Events & Experiences' })
    : intl.formatMessage({ defaultMessage: 'Academy Education' });

  // Brand slug (simplified - you may want to add a brands map)
  const brandSlug = (educationReward.brand && educationReward.brand.n && educationReward.brand.n.toLowerCase().replace(/\s+/g, '-')) || 'brands';

  const classData = {
    id: educationReward._id,
    name: educationReward.n || 'Class Name',
    brand: (educationReward.brand && educationReward.brand.n) || 'Club H',
    brandSlug,
    points: (educationReward.ecommerce && educationReward.ecommerce.price && educationReward.ecommerce.price.amount) || 0,
    oldPoints: (educationReward.ecommerce && educationReward.ecommerce.fullPrice && educationReward.ecommerce.fullPrice.amount) || null,
    category: educationData.category,
    classType,
    location: formattedLocation,
    date: formattedDate,
    time: formattedTime,
    distributor: educationData.distributor || '',
    classNumber: (educationReward.data && educationReward.data.idh) || (educationReward._id && educationReward._id.slice(-6)) || '000000',
    bookingLink: educationData.bookingLink,
    images,
    whatYouLearn,
    walkAwayWith: walkAwayWithList,
    exclusiveInsight: null, // Can be added to CMS if needed
  };
  
  // Set breadcrumb parent based on category
  const breadcrumbParent = isEvent
    ? {
        to: '/education/events-experiences',
        label: intl.formatMessage({ defaultMessage: 'Events & Experiences' }),
      }
    : {
        to: '/education/academy-education',
        label: intl.formatMessage({ defaultMessage: 'Academy Education' }),
      };

  const isFavorite = (wishlistData?.me?.wishlist?.items || []).some(
    (itm) => itm?.product?._id === educationReward?._id,
  );

  const handleSaveSeat = () => {
    // Navigate directly to attendees page — cart add happens on Continue
    navigate(`/${intl.locale}/attendees/${educationReward._id}`);
  };

  const toggleFavorite = async () => {
    if (user?._id == null) {
      return navigate(
        `/${intl.locale}/auth?redirect=${encodeURIComponent(location.pathname)}`,
      );
    }
    toast.dismiss();
    if (isFavorite) {
      toast.warn(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} removed from wishlist."
            values={{ productName: educationReward?.n }}
          />
        </ToastNotification>,
      );
      await removeFromWishlist({ variables: { productId: educationReward._id, locale } });
    } else {
      toast.success(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} added to wishlist."
            values={{ productName: educationReward?.n }}
          />
        </ToastNotification>,
      );
      await addToWishlist({ variables: { productId: educationReward._id, locale } });
    }
  };

  return (
    <>
      <Helmet>
        <title>{classData.name} - {classData.brand}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { to: '/', label: intl.formatMessage({ defaultMessage: 'Home' }) },
          { to: '/education', label: intl.formatMessage({ defaultMessage: 'Education' }) },
          breadcrumbParent,
          { to: null, label: isEvent ? 'Event Name' : classData.name },
        ]}
      />

      <Root>
        <Container>
          <ImageSection>
            <ImageGallery>
              <ThumbnailList>
                {classData.images.map((image, index) => (
                  <Thumbnail
                    key={index}
                    $active={selectedImageIndex === index}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img src={image} alt={`Thumbnail ${index + 1}`} />
                  </Thumbnail>
                ))}
              </ThumbnailList>
              <MainImage>
                <img src={classData.images[selectedImageIndex]} alt={classData.name} />
              </MainImage>
            </ImageGallery>
          </ImageSection>

          <DetailsSection>
            <BrandName to={`/brands/${classData.brandSlug}`}>
              {classData.brand}
            </BrandName>
            <ClassName>{classData.name}</ClassName>
            <Points>
              {classData.oldPoints != null && (
                <span
                  style={{
                    textDecoration: 'line-through',
                    marginRight: 8,
                  }}
                >
                  {classData.oldPoints.toLocaleString()} PTS
                </span>
              )}
              {classData.points.toLocaleString()} PTS
            </Points>

            <DetailsList>
              <DetailsItem>
                <DetailsLabel>
                  <FormattedMessage defaultMessage="Type:" />
                </DetailsLabel>
                <DetailsValue>{classData.classType}</DetailsValue>
              </DetailsItem>

              <DetailsItem>
                <DetailsLabel>
                  <FormattedMessage defaultMessage="Location:" />
                </DetailsLabel>
                <DetailsValue>{classData.location}</DetailsValue>
              </DetailsItem>

              <DetailsItem>
                <DetailsLabel>
                  <FormattedMessage defaultMessage="Date:" />
                </DetailsLabel>
                <DetailsValue>{classData.date}</DetailsValue>
              </DetailsItem>

              <DetailsItem>
                <DetailsLabel>
                  <FormattedMessage defaultMessage="Time:" />
                </DetailsLabel>
                <DetailsValue>{classData.time}</DetailsValue>
              </DetailsItem>

              {!isEvent && (
                <DetailsItem>
                  <DetailsLabel>
                    <FormattedMessage defaultMessage="Distributor:" />
                  </DetailsLabel>
                  <DetailsValue>{classData.distributor}</DetailsValue>
                </DetailsItem>
              )}

              <DetailsItem>
                <DetailsLabel>
                  <FormattedMessage defaultMessage="Class Number:" />
                </DetailsLabel>
                <DetailsValue>{classData.classNumber}</DetailsValue>
              </DetailsItem>
            </DetailsList>

            <ButtonGroup>
              {(() => {
                const qualifyingTiers = Array.isArray(educationData.qualifyingTiers)
                  ? educationData.qualifyingTiers
                  : [];
                const isTierRestrictedReward =
                  Array.isArray(educationData.qualifyingTiers) &&
                  qualifyingTiers.length > 0;

                const computedTier =
                  user?._id != null
                    ? normalizeLoyaltyTier(loyaltyTierData?.me?.loyaltyTier)
                    : null;

                // Pass through only for non-logged-in users. For logged-in users,
                // treat a still-loading tier as blocked to prevent the race condition window.
                const isEligibleForTrip =
                  !isTierRestrictedReward ||
                  (user?._id == null) ||
                  (!loyaltyTierLoading &&
                    computedTier != null &&
                    qualifyingTiers.some(
                      (t) => t.toLowerCase() === computedTier.toLowerCase(),
                    ));

                const redeemDisabled =
                  user?._id != null &&
                  isTierRestrictedReward &&
                  (loyaltyTierLoading || !isEligibleForTrip);
                const redeemDisabledMessage = redeemDisabled
                  ? formatEligibleTiersMessage(qualifyingTiers)
                  : null;

                return (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <SaveButton
                        onClick={() => {
                          if (redeemDisabled) {
                            toast.dismiss();
                            toast.warn(
                              <ToastNotification>{redeemDisabledMessage}</ToastNotification>,
                            );
                            return;
                          }
                          handleSaveSeat();
                        }}
                        disabled={redeemDisabled}
                      >
                        {isEvent ? (
                          <FormattedMessage defaultMessage="ADD TO CART" />
                        ) : (
                          <FormattedMessage defaultMessage="SAVE MY SEAT" />
                        )}
                        <Icon icon="arrow-right" />
                      </SaveButton>
                      {redeemDisabled && redeemDisabledMessage && (
                        <Typography
                          type="text-secondary"
                          style={{ color: '#e1000f', fontWeight: 700 }}
                        >
                          {redeemDisabledMessage}
                        </Typography>
                      )}
                    </div>
                  </>
                );
              })()}
              <FavoriteButton onClick={toggleFavorite} $isFavorite={isFavorite}>
                <Icon icon={isFavorite ? 'heart-filled' : 'heart'} />
              </FavoriteButton>
            </ButtonGroup>

            <TicketNote>
              <FormattedMessage defaultMessage="Your ticket count & attendee information will be completed on the next page" />
            </TicketNote>

          {classData.bookingLink && (
            <DescriptionSection>
              <SectionTitle>
                <FormattedMessage defaultMessage="Not enough points?" />
              </SectionTitle>
              <p>
                {classData.bookingLink ? (
                  <a 
                    href={classData.bookingLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#e1000f', textDecoration: 'none', fontWeight: 'bold' }}
                  >
                    <FormattedMessage defaultMessage="Book" />
                  </a>
                ) : (
                  <strong>
                    <FormattedMessage defaultMessage="Book" />
                  </strong>
                )}{' '}
                <FormattedMessage defaultMessage="directly with our distributor partner" />
              </p>
            </DescriptionSection>
            )}

            <DescriptionSection>
              <Typography
                type="text-primary"
                dangerouslySetInnerHTML={{ __html: classData.whatYouLearn }}
              />
            </DescriptionSection>

            <DescriptionSection>
              {classData.exclusiveInsight && (
                <Typography
                  type="text-primary"
                  style={{ marginTop: '16px', fontStyle: 'italic' }}
                  dangerouslySetInnerHTML={{ __html: classData.exclusiveInsight }}
                />
              )}
            </DescriptionSection>
          </DetailsSection>
        </Container>
      </Root>
    </>
  );
};
